import { Alert, Box, Button, CircularProgress, FormHelperText, TextField, Typography } from '@mui/material';
import { JSX, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useBranding } from '../../../contexts/BrandingContext';
import { loginLink, myAppointmentsLink } from '../../../library/constants';
import { formatDayLabel } from '../../../library/dateTimeHelper';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { localised } from '../../../library/models/localisedText';
import {
    AccountMode,
    IVerificationPreview,
    IVerifyBookingRequest,
    IVerifyBookingResponse,
} from '../../../library/models/publicBooking';
import { useApiFormError } from '../../../library/useApiFormError';
import { useRefreshDiary } from '../../../library/useRefreshDiary';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { clockOf } from '../AppointmentBookingForm/slotGrouping';
import { clearDraft, saveDraft } from './bookingDraft';
import { publicBookingLink } from './paths';
import { createVerifyBookingSchema, VerifyBookingValues } from './verifyBookingSchema';
import {
    actionsStyles,
    alertStyles,
    fieldStyles,
    instructionStyles,
    loadingBoxStyles,
    panelStyles,
    summaryStyles,
    titleStyles,
} from './verifyBooking.styles';

/** A link that can no longer be confirmed, and the one sentence to say about it. */
type Dead = 'INVALID' | 'EXPIRED' | 'USED' | 'NOT_ELIGIBLE';

const DEAD_MESSAGES: Record<Dead, string> = {
    INVALID: ResourcesIds.PUBLIC_BOOKING_VERIFY_INVALID,
    EXPIRED: ResourcesIds.PUBLIC_BOOKING_VERIFY_EXPIRED,
    USED: ResourcesIds.PUBLIC_BOOKING_VERIFY_USED,
    NOT_ELIGIBLE: ResourcesIds.PUBLIC_BOOKING_VERIFY_NOT_ELIGIBLE,
};

/**
 * The page the confirmation email opens: `/book/verify#<token>`.
 *
 * The token is in the **fragment**, which a browser never sends anywhere -- not to the web
 * server's logs, not in a Referer -- and reaches the API only in a request body. Opening the
 * link changes nothing: it previews which request this is and which password to ask for, and
 * only a typed password confirms it. That is also what keeps a mail scanner that follows every
 * link from confirming anything on the person's behalf.
 *
 * Both good outcomes sign the person in. BOOKED lands on their appointments; SLOT_UNAVAILABLE --
 * the time went while the email sat unread -- lands back on the slot step with the service and
 * therapist still chosen, now as a signed-in patient.
 */
export const VerifyBooking = (): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { adoptSession } = useAuth();
    const { hostname } = useBranding();
    const { showSnackbar } = useSnackbar();
    const refreshDiary = useRefreshDiary();

    const [token] = useState<string>(() => location.hash.replace(/^#/, ''));
    const [dead, setDead] = useState<Dead | null>(token === '' ? 'INVALID' : null);

    const preview = useReactMutation<IVerificationPreview, Error, string>(
        services.publicBooking.previewMutation()
    );
    const verify = useReactMutation<IVerifyBookingResponse, Error, IVerifyBookingRequest>(
        services.publicBooking.verifyMutation()
    );

    // Asked once, on arrival. A mutation rather than a query because the token travels in a
    // body, and so nothing about it is ever cached under a key.
    const { mutate: askPreview } = preview;
    useEffect(() => {
        if (token !== '') {
            askPreview(token, {
                onSuccess: (answer: IVerificationPreview) => {
                    if (answer.state !== 'READY') {
                        setDead(answer.state);
                    }
                },
                onError: () => setDead('INVALID'),
            });
        }
    }, [token, askPreview]);

    const onPassword = (password: string, report: (error: unknown) => void): void => {
        verify.mutate({ token, password }, {
            onSuccess: (response: IVerifyBookingResponse) => {
                switch (response.outcome) {
                    case 'BOOKED':
                        if (response.auth) {
                            adoptSession(response.auth);
                        }
                        clearDraft(hostname);
                        refreshDiary();
                        showSnackbar(translate(ResourcesIds.APPOINTMENT_REQUEST_SENT), 'success');
                        navigate(myAppointmentsLink, { replace: true });
                        return;
                    case 'SLOT_UNAVAILABLE':
                        if (response.auth) {
                            adoptSession(response.auth);
                        }
                        if (response.serviceId) {
                            saveDraft(hostname, {
                                serviceId: response.serviceId,
                                therapistId: response.therapistId ?? null,
                                slot: null,
                            });
                        }
                        showSnackbar(translate(ResourcesIds.PUBLIC_BOOKING_SLOT_TAKEN), 'warning');
                        navigate(publicBookingLink, { replace: true });
                        return;
                    case 'ALREADY_USED':
                        setDead('USED');
                        return;
                    default:
                        setDead(response.outcome);
                }
            },
            onError: report,
        });
    };

    if (dead) {
        return (
            <Box sx={panelStyles}>
                <Typography variant="h4" component="h1" sx={titleStyles}>
                    {translate(ResourcesIds.PUBLIC_BOOKING_VERIFY_TITLE)}
                </Typography>
                <Alert severity={dead === 'USED' ? 'info' : 'warning'} sx={alertStyles}>
                    {translate(DEAD_MESSAGES[dead])}
                </Alert>
                <Box sx={actionsStyles}>
                    {dead === 'USED' ? (
                        <Button variant="contained" onClick={() => navigate(loginLink)}>
                            {translate(ResourcesIds.SIGN_IN_BUTTON)}
                        </Button>
                    ) : dead !== 'NOT_ELIGIBLE' && (
                        <Button variant="contained" onClick={() => navigate(publicBookingLink)}>
                            {translate(ResourcesIds.PUBLIC_BOOKING_CHOOSE_AGAIN)}
                        </Button>
                    )}
                </Box>
            </Box>
        );
    }

    if (!preview.data) {
        return (
            <Box sx={loadingBoxStyles}>
                <CircularProgress />
            </Box>
        );
    }

    const request: IVerificationPreview = preview.data;
    const therapist: string = request.therapist
        ? `${request.therapist.firstName} ${request.therapist.lastName}`.trim()
        : '';
    const when: string = request.startsAt && request.endsAt
        ? `${formatDayLabel(request.startsAt.slice(0, 10), i18n)}, `
            + `${clockOf(request.startsAt)} - ${clockOf(request.endsAt)}`
        : '';

    return (
        <Box sx={panelStyles}>
            <Typography variant="h4" component="h1" sx={titleStyles}>
                {translate(ResourcesIds.PUBLIC_BOOKING_VERIFY_TITLE)}
            </Typography>

            <Typography sx={summaryStyles}>
                {translate(ResourcesIds.PUBLIC_BOOKING_VERIFY_SUMMARY, {
                    service: request.serviceNameRo
                        ? localised(request.serviceNameRo, request.serviceNameEn, i18n.language)
                        : '',
                    therapist,
                    when,
                })}
            </Typography>

            <Typography sx={instructionStyles}>
                {request.mode !== 'ENTER_PASSWORD'
                    ? translate(ResourcesIds.PUBLIC_BOOKING_VERIFY_CHOOSE_PASSWORD)
                    : translate(ResourcesIds.PUBLIC_BOOKING_VERIFY_ENTER_PASSWORD,
                        { email: request.emailAddress })}
            </Typography>

            <VerifyPasswordForm
                mode={request.mode ?? 'CHOOSE_PASSWORD'}
                emailAddress={request.emailAddress ?? ''}
                isPending={verify.isPending}
                onPassword={onPassword}
            />
        </Box>
    );
};

interface VerifyPasswordFormProps {
    mode: AccountMode;
    emailAddress: string;
    isPending: boolean;
    onPassword: (password: string, report: (error: unknown) => void) => void;
}

/**
 * The password half, mounted only once the preview has said which password to ask for --
 * the schema is built from that answer, and a form's resolver is fixed when it mounts.
 */
const VerifyPasswordForm = ({ mode, emailAddress, isPending, onPassword }: VerifyPasswordFormProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const schema = useMemo(() => createVerifyBookingSchema(translate, mode), [translate, mode]);

    const { register, handleSubmit, setError, formState: { errors, isValid } } =
        useValidatedForm<VerifyBookingValues>(schema, {
            defaultValues: { password: '', confirmPassword: '' },
        });

    const apiError = useApiFormError(setError, Object.keys(schema.fields), translate,
        ResourcesIds.PUBLIC_BOOKING_VERIFY_FAILED);

    const onValid = (values: VerifyBookingValues): void => {
        apiError.clear();
        onPassword(values.password, apiError.report);
    };

    return (
        <>
            {apiError.formError && (
                <Alert severity="error" sx={alertStyles}>{apiError.formError}</Alert>
            )}

            <Box component="form" noValidate onSubmit={handleSubmit(onValid)}>
                {/* The address, for the browser's password manager to file the new password under. */}
                <input type="hidden" name="username" autoComplete="username" value={emailAddress} />

                <TextField
                    fullWidth
                    type="password"
                    autoComplete={mode === 'CHOOSE_PASSWORD' ? 'new-password' : 'current-password'}
                    sx={fieldStyles}
                    label={translate(ResourcesIds.PASSWORD_LABEL)}
                    {...register('password')}
                    {...fieldProps(errors, 'password')}
                />

                {mode === 'CHOOSE_PASSWORD' && (
                    <TextField
                        fullWidth
                        type="password"
                        autoComplete="new-password"
                        sx={fieldStyles}
                        label={translate(ResourcesIds.CONFIRM_PASSWORD_LABEL)}
                        {...register('confirmPassword')}
                        {...fieldProps(errors, 'confirmPassword')}
                    />
                )}

                <Box sx={actionsStyles}>
                    <Button type="submit" variant="contained" disabled={!isValid || isPending}>
                        {translate(ResourcesIds.PUBLIC_BOOKING_VERIFY_CONFIRM)}
                    </Button>
                    {!isValid && (
                        <FormHelperText>{translate(ResourcesIds.FORM_BLOCKED_SAVE)}</FormHelperText>
                    )}
                </Box>
            </Box>
        </>
    );
};
